import { hydrateIntoContainer, renderIntoContainer, unmountContainer } from 'react/root/containerRoot';
import pageEntries from 'navigation/pageEntries';
import fillAdSlots from 'navigation/adSlots';

// Brings a page area (#page_content) to life: mounts each React root in it from
// its props script, hydrating what the ssr service rendered, and fills its ad
// slots. deactivatePageArea takes it all down before client navigation swaps
// the area for the next page's.
const mountedByArea = new WeakMap();

function mountedIn(area) {
	if(!mountedByArea.has(area)) {
		mountedByArea.set(area, []);
	}
	return mountedByArea.get(area);
}

async function mountRoot(root, area, options) {
	const loadEntry = pageEntries[root.getAttribute('data-entry')];
	const propsScript = root.ownerDocument.getElementById(`${root.id}-props`);
	if(!loadEntry || !propsScript) {
		return;
	}
	const props = JSON.parse(propsScript.textContent);
	const entry = await loadEntry();
	// The area may have been taken down while the page's code loaded.
	if(!area.contains(root) || mountedIn(area).some((mounted) => mounted.root === root)) {
		return;
	}
	const element = entry.element(props, area, options);
	if(root.hasAttribute('data-server-rendered')) {
		hydrateIntoContainer(element, root);
	}
	else {
		renderIntoContainer(element, root);
	}
	const cleanup = entry.activate ? entry.activate(area, props) : null;
	mountedIn(area).push({ root, cleanup });
}

// Loads the code of the pages html holds, so client navigation can swap it in
// ready to mount instead of showing empty roots while the chunks download. A
// chunk that fails to load is left for mountRoot to meet.
export function preparePageContent(html) {
	const template = document.createElement('template');
	template.innerHTML = html;
	const names = new Set([...template.content.querySelectorAll('[data-entry]')].map((root) => root.getAttribute('data-entry')));
	return Promise.all([...names]
		.filter((name) => pageEntries[name])
		.map((name) => pageEntries[name]().catch(() => null)));
}

// restored: the navigator showed this page from its cache, so the props may be
// stale; entries can fetch fresh ones.
export async function activatePageArea(area, { restored = false } = {}) {
	fillAdSlots(area);
	await Promise.all([...area.querySelectorAll('[data-entry]')].map((root) => mountRoot(root, area, { restored })));
}

export function deactivatePageArea(area) {
	mountedIn(area).forEach(({ root, cleanup }) => {
		if(typeof cleanup === 'function') {
			cleanup();
		}
		unmountContainer(root);
	});
	mountedByArea.delete(area);
}
